<template>
  <div class="app-activation-personal-data-input">
    <form class="personal-data-input-form" :id="'form' + _uid" novalidate data-form-activation-personal-data-input>
      <span class="text-sm required-field-hint">*Pflichtfeld</span>
      <div class="container-box">
        <div class="content-limiter">
          <h2 class="text-3xl">Deine Daten</h2>
          <p class="py-lg">
            Die <abbr title="Subscriber Identity Module">SIM</abbr>-Karten Aktivierung erfolgt nur nach erfolgreicher
            persönlicher Identifikation. Die hier eingegebenen Daten müssen exakt mit denen deines Ausweisdokuments
            übereinstimmen.
          </p>

          <fieldset class="inputs-container">
            <legend class="text-xl visually-hidden">Persönliche Daten</legend>
            <div class="inputs-container">
              <h2 class="visually-hidden">Persönliche Daten</h2>

              <div class="input-field">
                <label class="text-base-medium" for="firstName">
                  Anrede<span class="required" aria-hidden="true">*</span>
                </label>
                <div class="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="gender"
                      value="FEMALE"
                      v-model="person.gender"
                      required
                      @input="$v.person.gender.$touch"
                      aria-describedby="gender-hint"
                    />
                    <span class="custom-radio"></span>
                    <span class="text-sm">Frau</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="gender"
                      value="MALE"
                      v-model="person.gender"
                      required
                      @input="$v.person.gender.$touch"
                      aria-describedby="gender-hint"
                    />
                    <span class="custom-radio"></span>
                    <span class="text-sm">Herr</span>
                  </label>
<!--                  <label>--> <!-- Wurde vorerst ausgeblendet. Kann eingeblendet werden, sobald Magma die Option Divers hat  -->
<!--                    <input-->
<!--                      type="radio"-->
<!--                      name="gender"-->
<!--                      value="DIVERSE"-->
<!--                      v-model="person.gender"-->
<!--                      required-->
<!--                      @input="$v.person.gender.$touch"-->
<!--                      aria-describedby="gender-hint"-->
<!--                    />-->
<!--                    <span class="custom-radio"></span>-->
<!--                    <span class="text-sm">Divers</span>-->
<!--                  </label>-->
                </div>
                <small id="gender-hint" class="sr-only">Pflichtfeld</small>
                <div id="gender-error" class="error-feedback" v-if="!isValid('gender', 'person') && $v.person.gender.$dirty">
                  <span v-if="$v.person.gender.required === false"> Dieses Feld ist ein Pflichtfeld<br /> </span>
                </div>
              </div>

              <div class="input-field">
                <label class="text-base-medium" for="firstName">
                  Vorname<span class="required" aria-hidden="true">*</span>
                </label>
                <ValidationIndicator :dirty="$v.person.firstName.$dirty" :valid="isValid('firstName', 'person')">
                  <input
                    id="firstName"
                    name="firstName"
                    placeholder="Vorname"
                    type="text"
                    class="input"
                    v-model="person.firstName"
                    pattern="[a-zA-Z]*"
                    required
                    @blur="$v.person.firstName.$touch"
                    @click="$v.person.firstName.$reset"
                    :class="{
                      'input--invalid': $v.person.firstName.$dirty && !isValid('firstName', 'person'),
                      'input--valid': $v.person.firstName.$dirty && isValid('firstName', 'person')
                    }"
                    aria-describedby="firstname-hint"
                    aria-errormessage="firstname-error"
                  />
                </ValidationIndicator>
                <small id="firstname-hint" class="sr-only">Pflichtfeld</small>
                <div
                  id="firstname-error"
                  class="error-feedback"
                  v-if="!isValid('firstName', 'person') && $v.person.firstName.$dirty"
                >
                  <span v-if="$v.person.firstName.required === false"> Bitte gib deinen Vornamen ein.<br /> </span>
                  <span v-if="$v.person.firstName.alphaSpaces === false">
                    Der Vorname darf nur Buchstaben und Leerzeichen enthalten.<br />
                  </span>
                </div>
              </div>

              <div class="input-field">
                <label class="text-base-medium" for="lastName">
                  Nachname<span class="required" aria-hidden="true">*</span>
                </label>
                <ValidationIndicator :dirty="$v.person.lastName.$dirty" :valid="isValid('lastName', 'person')">
                  <input
                    id="lastName"
                    name="lastName"
                    placeholder="Nachname"
                    type="text"
                    class="input"
                    v-model="person.lastName"
                    required
                    @blur="$v.person.lastName.$touch"
                    @click="$v.person.lastName.$reset"
                    :class="{
                      'input--invalid': $v.person.lastName.$dirty && !isValid('lastName', 'person'),
                      'input--valid': $v.person.lastName.$dirty && isValid('lastName', 'person')
                    }"
                    aria-describedby="lastname-hint"
                    aria-errormessage="lastname-error"
                  />
                </ValidationIndicator>
                <small id="lastname-hint" class="sr-only">Pflichtfeld</small>
                <div
                  id="lastname-error"
                  class="error-feedback"
                  v-if="!isValid('lastName', 'person') && $v.person.lastName.$dirty"
                >
                  <span v-if="$v.person.lastName.required === false"> Bitte gib deinen Nachnamen ein.<br /> </span>
                  <span v-if="$v.person.lastName.alphaSpaces === false">
                    Der Nachname darf nur Buchstaben und Leerzeichen enthalten.<br />
                  </span>
                </div>
              </div>
            </div>
          </fieldset>

          <fieldset class="inputs-container">
            <legend class="text-xl visually-hidden">Adressdaten</legend>
            <div class="inputs-container">
              <h2 class="visually-hidden">Adressdaten</h2>

              <div class="input-group">
                <div class="input-field input-field--fixed">
                  <label class="text-base-medium" for="street">
                    Straße<span class="required" aria-hidden="true">*</span>
                  </label>
                  <ValidationIndicator :dirty="$v.address.street.$dirty" :valid="isValid('street', 'address')">
                    <input
                      id="street"
                      name="street"
                      placeholder="Straße"
                      type="text"
                      class="input"
                      v-model="address.street"
                      required
                      @blur="$v.address.street.$touch"
                      @click="$v.address.street.$reset"
                      :class="{
                        'input--invalid': $v.address.street.$dirty && !isValid('street', 'address'),
                        'input--valid': $v.address.street.$dirty && isValid('street', 'address')
                      }"
                      aria-describedby="street-hint"
                      aria-errormessage="street-error"
                    />
                  </ValidationIndicator>
                  <small id="street-hint" class="sr-only">Pflichtfeld</small>
                  <div
                    id="street-error"
                    class="error-feedback"
                    v-if="!isValid('street', 'address') && $v.address.street.$dirty"
                  >
                    <span v-if="$v.address.street.required === false"> Bitte gib deine Straße ein.<br /> </span>
                  </div>
                </div>

                <div class="input-field input-field--fluid">
                  <label class="text-base-medium" for="houseNumber">
                    Hausnummer<span class="required" aria-hidden="true">*</span>
                  </label>
                  <ValidationIndicator :dirty="$v.address.houseNumber.$dirty" :valid="isValid('houseNumber', 'address')">
                    <input
                      id="houseNumber"
                      name="houseNumber"
                      placeholder="Hausnummer"
                      type="text"
                      class="input"
                      v-model="address.houseNumber"
                      required
                      @blur="$v.address.houseNumber.$touch"
                      @click="$v.address.houseNumber.$reset"
                      :class="{
                        'input--invalid': $v.address.houseNumber.$dirty && !isValid('houseNumber', 'address'),
                        'input--valid': $v.address.houseNumber.$dirty && isValid('houseNumber', 'address')
                      }"
                      aria-describedby="houseNumber-hint"
                    />
                  </ValidationIndicator>
                  <small id="houseNumber-hint" class="sr-only">Pflichtfeld</small>
                  <div
                    id="houseNumber-error"
                    class="error-feedback"
                    v-if="!isValid('houseNumber', 'address') && $v.address.houseNumber.$dirty"
                  >
                    <span v-if="$v.address.houseNumber.required === false"> Bitte gib deine Hausnummer ein.“<br /> </span>
                    <span v-if="$v.address.houseNumber.alphaNumeric === false">
                      Deine Haunsummer darf nur Buchstaben und Zahlen beinhalten<br />
                    </span>
                    <span v-if="$v.address.houseNumber.maxLength === false">
                      Deine Hausnummer darf maximal neunstellig sein<br />
                    </span>
                  </div>
                </div>
              </div>

              <div class="input-field">
                <label class="text-base-medium" for="supplement">
                  Adresszusatz
                </label>
                <input
                  id="supplement"
                  name="supplement"
                  placeholder="Adresszusatz"
                  type="text"
                  class="input"
                  v-model="address.supplement"
                />
              </div>

              <div class="input-group">
                <div class="input-field input-field--fluid">
                  <label class="text-base-medium flex-align-center" for="zip">
                    <abbr title="Postleitzahl">PLZ</abbr><span class="required" aria-hidden="true">*</span>
                  </label>
                  <ValidationIndicator :dirty="$v.address.zip.$dirty" :valid="isValid('zip', 'address')">
                    <input
                      id="zip"
                      name="zip"
                      placeholder="PLZ"
                      type="text"
                      class="input"
                      v-model.trim="address.zip"
                      required
                      @blur="$v.address.zip.$touch"
                      @click="$v.address.zip.$reset"
                      :class="{
                        'input--invalid': $v.address.zip.$dirty && !isValid('zip', 'address'),
                        'input--valid': $v.address.zip.$dirty && isValid('zip', 'address')
                      }"
                      aria-describedby="zip-hint"
                      aria-errormessage="zip-error"
                    />
                  </ValidationIndicator>
                  <small id="zip-hint" class="sr-only">Pflichtfeld</small>
                  <div id="zip-error" class="error-feedback" v-if="!isValid('zip', 'address') && $v.address.zip.$dirty">
                    <span v-if="$v.address.zip.required === false"> Bitte gib deine Postleitzahl ein.<br /> </span>
                    <span v-if="$v.address.zip.validZipCode === false">
                      Die Postleitzahl darf nur aus Zahlen bestehen und muss fünfstellig sein.<br />
                    </span>
                  </div>
                </div>

                <div class="input-field input-field--fixed">
                  <label class="text-base-medium" for="city"> Ort<span class="required" aria-hidden="true">*</span> </label>
                  <ValidationIndicator :dirty="$v.address.city.$dirty" :valid="isValid('city', 'address')">
                    <input
                      id="city"
                      name="city"
                      placeholder="Ort"
                      type="text"
                      class="input"
                      v-model="address.city"
                      required
                      @blur="$v.address.city.$touch"
                      @click="$v.address.city.$reset"
                      :class="{
                        'input--invalid': $v.address.city.$dirty && !isValid('city', 'address'),
                        'input--valid': $v.address.city.$dirty && isValid('city', 'address')
                      }"
                      aria-describedby="city-hint"
                      aria-errormessage="city-error"
                    />
                  </ValidationIndicator>
                  <small id="city-hint" class="sr-only">Pflichtfeld</small>
                  <div id="city-error" class="error-feedback" v-if="!isValid('city', 'address') && $v.address.city.$dirty">
                    <span v-if="$v.address.city.required === false"> Bitte gib deinen Wohnort ein.<br /> </span>
                    <span v-if="$v.address.city.validCityString === false">
                      Der Ortsname darf nur Buchstaben, Leerzeichen und bestimmte Sonderzeichen enthalten.<br />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>

          <fieldset class="inputs-container">
            <legend class="text-xl visually-hidden">Geburtsdatum</legend>
            <div class="inputs-container">
              <h2 class="visually-hidden">Persönliche Informationen</h2>

              <div class="input-field">
                <label class="text-base-medium" for="birthdate">
                  Geburtsdatum<span class="required" aria-hidden="true">*</span>
                </label>
                <ValidationIndicator :dirty="$v.person.birthDate.$dirty" :valid="isValid('birthDate', 'person')">
                  <input
                    id="birthdate"
                    type="text"
                    name="birthDate"
                    placeholder="TT.MM.JJJJ  (z.B. 10.12.1988)"
                    class="input"
                    v-model="person.birthDate"
                    required
                    @blur="$v.person.birthDate.$touch"
                    @click="$v.person.birthDate.$reset"
                    :class="{
                      'input--invalid': $v.person.birthDate.$dirty && !isValid('birthDate', 'person'),
                      'input--valid': $v.person.birthDate.$dirty && isValid('birthDate', 'person')
                    }"
                    autocomplete="off"
                    aria-errormessage="birthdate-error"
                    aria-describedby="birthdate-hint"
                  />
                </ValidationIndicator>

                <small id="birthdate-hint" class="sr-only">
                  <p>Pflichtfeld</p>
                  <p>
                    Bitte geben Sie das Datum im Format <strong>TT.MM.JJJJ</strong> ein. Zwei Ziffern für den Tag, Punkt, zwei
                    Ziffern für den Monat, Punkt, vier Ziffern für das Jahr.
                  </p>
                </small>
                <div
                  id="birthdate-error"
                  class="error-feedback"
                  v-if="!isValid('birthDate', 'person') && $v.person.birthDate.$dirty"
                >
                  <span v-if="$v.person.birthDate.required === false">
                    Bitte gib dein Geburtsdatum ein.
                  </span>
                  <span v-if="$v.person.birthDate.minAge === false">
                    Du musst mindestens {{ alowedMinAge }} Jahre alt sein.<br />
                  </span>
                  <span v-if="$v.person.birthDate.validDateFormat === false">
                    Ungültiges Format. Bitte gib dein Geburtsdatum im Format TT.MM.JJJJ ein (z. B. 15.08.2005).
                  </span>
                </div>
              </div>
            </div>
          </fieldset>

          <fieldset class="inputs-container">
            <legend class="text-xl visually-hidden">E-Mail-Adresse</legend>
            <div class="inputs-container">
              <h2 class="visually-hidden">Kontaktdaten</h2>

              <div class="input-field">
                <label class="text-base-medium" for="email">
                  E-Mail-Adresse<span class="required" aria-hidden="true">*</span>
                </label>
                <ValidationIndicator :dirty="$v.additional.email.$dirty" :valid="isValid('email', 'additional')">
                  <input
                    id="email"
                    name="email"
                    placeholder="z.B. info@mailadresse.de"
                    type="email"
                    class="input"
                    required
                    @blur="$v.additional.email.$touch"
                    @click="$v.additional.email.$reset"
                    :class="{
                      'input--invalid': $v.additional.email.$dirty && !isValid('email', 'additional'),
                      'input--valid': $v.additional.email.$dirty && isValid('email', 'additional')
                    }"
                    v-model="additional.email"
                    aria-describedby="email-hint"
                    aria-errormessage="email-error"
                  />
                </ValidationIndicator>
                <small id="email-hint" class="sr-only">Pflichtfeld</small>
                <div
                  id="email-error"
                  class="error-feedback"
                  v-if="!isValid('email', 'additional') && $v.additional.email.$dirty"
                >
                  <span v-if="$v.additional.email.required === false"> Bitte gib deine E-Mail-Adresse ein.<br /> </span>
                  <span v-if="$v.additional.email.email === false">
                    Bitte gib eine gültige E-Mail-Adresse ein (z.B. name@beispiel.de).<br />
                  </span>
                </div>
              </div>

              <div class="input-field">
                <label class="text-base-medium" for="email-repeat">
                  E-Mail-Adresse wiederholen<span class="required" aria-hidden="true">*</span>
                </label>
                <ValidationIndicator :dirty="$v.emailRepeat.$dirty" :valid="isValid('emailRepeat')">
                  <input
                    id="email-repeat"
                    name="email-repeat"
                    placeholder="z.B. info@mailadresse.de"
                    type="email"
                    class="input"
                    required
                    @blur="$v.emailRepeat.$touch"
                    @click="$v.emailRepeat.$reset"
                    :class="{
                      'input--invalid': $v.emailRepeat.$dirty && !isValid('emailRepeat'),
                      'input--valid': $v.emailRepeat.$dirty && isValid('emailRepeat')
                    }"
                    v-model="emailRepeat"
                    aria-describedby="email-repeat-hint"
                    aria-errormessage="email-repeat-error"
                  />
                </ValidationIndicator>
                <small id="email-repeat-hint" class="sr-only">Pflichtfeld</small>
                <div id="email-repeat-error" class="error-feedback" v-if="!isValid('emailRepeat') && $v.emailRepeat.$dirty">
                  <span v-if="$v.emailRepeat.required === false"> Bitte wiederhole deine E-Mail-Adresse.<br /> </span>
                  <span v-if="$v.emailRepeat.email === false">
                    Bitte gib eine gültige E-Mail-Adresse ein (z.B. name@beispiel.de).<br />
                  </span>
                  <span v-if="$v.emailRepeat.sameAsEmail === false">
                    Die eingegebenen E-Mail-Adressen stimmen nicht überein.<br />
                  </span>
                </div>
                <small class="text-sm">
                  Du erhälst an die eingegebene E-Mail-Adresse eine E-Mail mit einem Bestätigungslink.
                </small>
              </div>
            </div>
          </fieldset>

          <fieldset class="inputs-container">
            <legend class="text-xl visually-hidden">Passwort</legend>
            <div class="inputs-container">
              <h2 class="visually-hidden">Passwort</h2>

              <div class="input-field">
                <label class="text-base-medium" for="password">
                  Passwort<span class="required" aria-hidden="true">*</span>
                </label>
                <ValidationIndicator :dirty="$v.additional.password.$dirty" :valid="isValid('password', 'additional')">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="*********"
                    class="input"
                    required
                    @blur="$v.additional.password.$touch"
                    @click="$v.additional.password.$reset"
                    :class="{
                      'input--invalid': $v.additional.password.$dirty && !isValid('password', 'additional'),
                      'input--valid': $v.additional.password.$dirty && isValid('password', 'additional')
                    }"
                    v-model="additional.password"
                    aria-describedby="password-hint"
                    aria-errormessage="password-error"
                  />
                </ValidationIndicator>

                <div
                  id="password-error"
                  class="error-feedback"
                  v-if="!isValid('password', 'additional') && $v.additional.password.$dirty"
                >
                  <span v-if="$v.additional.password.required === false"> Bitte gib ein Passwort ein.<br /> </span>
                  <span v-if="$v.additional.password.passwordValidation === false">
                    Das Passwort erfüllt nicht die erforderlichen Kriterien.<br />
                  </span>
                </div>

                <small class="text-sm">
                  Dein Passwort muss mindestens 8 und höchstens 32 Zeichen haben. Zudem muss es einen Buchstaben, sowie eine
                  Ziffer oder eines der folgenden Sonderzeichen enthalten: # ( ) [ ] { } ? ! $ % &amp; / = * + ~ , . : &lt; &gt;
                  @ € - _ ^ §
                </small>
              </div>

              <div class="input-field">
                <label class="text-base-medium" for="password-repeat">
                  Passwort wiederholen<span class="required" aria-hidden="true">*</span>
                </label>
                <ValidationIndicator :dirty="$v.passwordRepeat.$dirty" :valid="isValid('passwordRepeat')">
                  <input
                    id="password-repeat"
                    name="password-repeat"
                    type="password"
                    placeholder="*********"
                    class="input"
                    required
                    @blur="$v.passwordRepeat.$touch"
                    @click="$v.passwordRepeat.$reset"
                    :class="{
                      'input--invalid': $v.passwordRepeat.$dirty && !isValid('passwordRepeat'),
                      'input--valid': $v.passwordRepeat.$dirty && isValid('passwordRepeat')
                    }"
                    v-model="passwordRepeat"
                    aria-describedby="password-repeat-hint"
                    aria-errormessage="password-repeat-error"
                  />
                </ValidationIndicator>

                <div
                  id="password-repeat-error"
                  class="error-feedback"
                  v-if="!isValid('passwordRepeat') && $v.passwordRepeat.$dirty"
                >
                  <span v-if="$v.passwordRepeat.required === false"> Bitte wiederhole dein Passwort.<br /> </span>
                  <span v-if="$v.passwordRepeat.sameAsPassword === false">
                    Die eingegebenen Passwörter stimmen nicht überein.<br />
                  </span>
                </div>
              </div>
              <small id="passpassword-repeatord-hint" class="sr-only">Pflichtfeld</small>
            </div>
          </fieldset>
          <ShowPrivilegeTerms></ShowPrivilegeTerms>
        </div>
      </div>
      <div class="container-box">
        <div class="content-limiter">
          <h2 class="text-3xl">Identifikationsverfahren</h2>
          <p>
            Seit Juli 2017 müssen sich alle neuen
            <span lang="en">Prepaid</span>-Kunden in Deutschland ausweisen. Das hat rechtliche Gründe. Gebe deine
            Staatsangehörigkeit und deinen Ausweistyp an und wähle deine bevorzugte Art der Identifikation.
          </p>
          <InputCountryData
            :data-match-ident.sync="identInformation.dataMatchIdent"
            :default-ident-card-key="identInformation.identCard.key"
            :default-nationality-key="identInformation.nationality.iso3"
            :ident-card.sync="identInformation.identCard"
            :nationality.sync="identInformation.nationality"
            :ident-type.sync="identInformation.identType"
            :invalid.sync="identInvalid"
            @validation="checkFormValidation"
            :errorMessage="displayErrorMessageCheckbox"
          >
          </InputCountryData>
        </div>
      </div>
      <div class="container-box" v-if="showEsim">
        <div class="content-limiter">
          <h2 class="text-3xl">SIM-Typ wählen</h2>
          <SimTypeSelection :sim-type.sync="simType" :show-esim="showEsim"> </SimTypeSelection>
        </div>
      </div>
      <div class="container-box" v-if="!showEsim">
        <div class="content-limiter">
          <h2 class="text-4xl">
            <abbr title="Embedded Subscriber Identity Module">e<span class="uppercase">Sim</span></abbr>
            Aktivierung
          </h2>
          <div class="info-box">
            <div class="flex-align-center">
              <span class="icon" aria-hidden="true"></span>
              <span class="text-base-semibold">Info</span>
            </div>
            <p class="text-sm">
              Eine Online-Aktivierung ist zur Zeit online nicht möglich und steht nur nach Abschluss des Aktivierungsprozesses
              zur Verfügung. Kontaktiere hierzu nach Abschluss unsere Kundenbetreuung:
            </p>
            <p class="text-sm">
              <a href="tel:02112774 2774" class="text-lg link-color">0211-2774 2774</a>
              <abbr class="text-xs super" title="Siehe Fußnote 1" aria-describedby="cs-footnote-1">1</abbr>
              oder
              <a href="tel:0172 222 20 22" class="text-lg link-color">0172 222 20 22</a>
              <abbr class="text-xs super" title="Siehe Fußnote 2" aria-describedby="cs-footnote-2">2</abbr>
            </p>
            <p class="text-sm">
              Als <span class="uppercase">Lidl</span> <span lang="en">Connect</span> Kunde nutzt du deine Inklusiveinheiten,
              wenn vorhanden. Andernfalls zahlst du in Deutschland 0,09 <abbr title="Euro">€</abbr> pro Minute.
            </p>
            <span class="text-xs">
              1. Es gilt der vom jeweiligen Anbieter ausgewiesene Preis für einen Anruf ins deutsche Festnetz.
            </span>
            <span class="text-xs">
              2. Aus Fremdnetzen gilt der vom jeweiligen Anbieter ausgewiesene Preis für einen Mobilfunkanruf ins
              <abbr title="deutsch">dt.</abbr> Vodafone-Netz.
            </span>
          </div>
        </div>
      </div>
      <FriendNotificationV2 :msisdn.sync="friendMsisdn" :process-name="'activation'">
        <template v-for="slot in possibleChildSlotsFor('Friend')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </FriendNotificationV2>
      <span class="text-sm required-field-hint">*Pflichtfeld</span>
      <Stepper @prev="back()" @next="save()" :loading-next="loading" :disabled-next="loading"> </Stepper>
    </form>
  </div>
</template>

<script>
import { required, sameAs, email, alphaNum, maxLength } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'
import { mapActions, mapMutations, mapGetters } from 'vuex'
import { validDate, validDateFormat, validGivenAge } from '@/validators/date'
import { validZipCode, validCityString } from '@/validators/address'
import InputCountryData from '@/components-v2/flows/LIDL/IdentMethod/InputCountryData.vue'
import ShowPrivilegeTerms from './ShowPrivilegeTerms.vue'
import processSteps from '@/mixins/processSteps'
import { stepName as previousProcessStep } from '../Info/ActivationInfo.vue'
import isValid from '@/mixins/isValid'
import slotProxy from '@/mixins/slotProxy'
import Stepper from '@/components-v2/generic/Stepper.vue'
import preFilledInput from '@/mixins/preFilledInput'
import preFilledObjectInput from '@/mixins/preFilledObjectInput'
import SimTypeSelection from '../Sim/SimTypeSelection'
import { SimTypes } from '@/helper/SimTypes'
import ValidationIndicator from '@/components-v2/generic/ValidationIndicator.vue'

const initialValues = {
  simType: SimTypes.simCardCode,
  identInformation: {
    nationality: {},
    identCard: {},
    identType: 'video',
    dataMatchIdent: false
  },
  address: {
    street: '',
    houseNumber: '',
    zip: '',
    city: '',
    supplement: ''
  },
  person: {
    gender: '',
    firstName: '',
    lastName: '',
    birthDate: null
  },
  additional: {
    email: '',
    password: ''
  }
}

// TODO: FIX
// eslint-disable-next-line no-useless-escape
const alphaSpaces = v => {
  if (!v) return false
  return /^([a-zA-Z -])*$/.test(v.replace(/\u200e/g, ''))
}

export const stepName = 'personalDataInput'
export default {
  name: 'EnterPersonalData',
  data() {
    return {
      brand_: null,
      identInvalid: true,
      displayErrorMessage: false,
      displayErrorMessageCheckbox: false,
      loading: false
    }
  },
  created() {
    this.brand_ = this.brand
  },
  components: {
    InputCountryData,
    ShowPrivilegeTerms,
    Stepper,
    SimTypeSelection,
    ValidationIndicator
  },
  methods: {
    ...mapMutations('activation', ['goToStep']),
    ...mapActions('activation/personalData', ['validate']),
    back() {
      this.goToStep(this.previousStep)
    },
    save() {
      if (this.loading) return
      this.$v.$touch()
      if (this.$v.$invalid) {
        if (this.$v.identInvalid.$invalid) {
          this.displayErrorMessageCheckbox = true
        }
        // won't find querySelector if not waiting for dom to be updated with nextTick
        this.$nextTick(() => {
          const firstError = document.querySelector('.input--invalid')
          const checkboxError = document.querySelector('.checkbox-class')
          if (firstError) firstError.focus()
          if (checkboxError && !firstError) checkboxError.focus()
        })
      } else {
        this.loading = true
        this.validate({
          person: this.person,
          address: this.address,
          additional: this.additional,
          identInformation: this.identInformation,
          friendMsisdn: this.friendMsisdn,
          simType: this.simType,
          nextStep: this.nextStep
        }).then(
          () => {
            this.loading = false
          },
          () => {
            this.loading = false
          }
        )
      }
    },
    selectDate(ctx) {
      if (ctx.selectedDate) {
        this.person.birthDate = ctx.selectedFormatted
      }
    },
    checkFormValidation() {
      this.$v.$touch()
    }
  },
  computed: {
    slotData() {
      return {
        alowedMinAge: this.alowedMinAge
      }
    },
    ...mapGetters('activation/personalData', {
      storedPerson: 'personal',
      storedAddress: 'address',
      storedAdditional: 'additional',
      storedIdentification: 'identificationFull',
      storedSimType: 'simType'
    }),
    ...mapGetters('friendNotification', {
      storedFriendMsisdn: 'existingCustomerMsisdn'
    }),
    ...mapGetters('activation/tariff', ['savedTariff']),
    ...mapGetters(['brand']),
    getPerson() {
      return { ...initialValues.person, ...this.storedPerson }
    },
    getAddress() {
      return { ...initialValues.address, ...this.storedAddress }
    },
    getAdditional() {
      return { ...initialValues.additional, ...this.storedAdditional }
    },
    getIdentification() {
      return { ...initialValues.identInformation, ...this.storedIdentification }
    },
    getSimType() {
      return this.storedSimType
    },
    calendarSettings() {
      return this.$config.calendar.calendarSettings
    }
  },
  props: {
    alowedMinAge: {
      type: Number,
      default: 18
    },
    showEsim: {
      type: Boolean,
      default: false
    }
  },
  mixins: [
    validationMixin,
    isValid,
    slotProxy(['Stepper']),
    processSteps(stepName, previousProcessStep),
    preFilledObjectInput('person', 'getPerson'),
    preFilledObjectInput('address', 'getAddress'),
    preFilledObjectInput('additional', 'getAdditional'),
    preFilledObjectInput('identInformation', 'getIdentification'),
    preFilledInput('friendMsisdn', 'storedFriendMsisdn'),
    preFilledInput('emailRepeat', function() {
      return this.storedAdditional?.email || ''
    }),
    preFilledInput('passwordRepeat', function() {
      return this.storedAdditional?.password || ''
    }),
    preFilledInput('simType', 'getSimType')
  ],
  validations() {
    const vm = this
    return {
      person: {
        gender: {
          required
        },
        firstName: {
          required,
          alphaSpaces
        },
        lastName: {
          required,
          alphaSpaces
        },
        birthDate: {
          validDate,
          validDateFormat,
          minAge(v) {
            return validGivenAge(v, vm.alowedMinAge)
          },
          birthDateYoungTariffValidation: this.$config?.validation?.validateYoungTariffBirthdate
            ? this.$config?.validation?.validateYoungTariffBirthdate(vm)
            : birthdate => true,
          required
        }
      },
      additional: {
        email: {
          required,
          email
        },
        password: {
          required,
          passwordValidation: this.$config.validation.validatePassword // also test length
        }
      },
      address: {
        street: {
          required
        },
        houseNumber: {
          required,
          alphaNum,
          maxLength: maxLength(9)
        },
        zip: {
          validZipCode,
          required
        },
        city: {
          validCityString,
          required
        }
      },
      identInvalid: {
        sameAs: sameAs(() => false)
      },
      emailRepeat: {
        required,
        email,
        sameAsEmail: sameAs(function() {
          return this.additional.email
        })
      },
      passwordRepeat: {
        required,
        sameAsPassword: sameAs(function() {
          return this.additional.password
        })
      },
      simType: {
        required
      }
    }
  }
}
</script>
